import { useEffect, useRef, useState, type ReactNode } from 'react';
import { usePhoneLayout } from './PhoneLayout';

/** A secondary page-level action. On a phone (below 768px) these collapse into the ⋯ menu (BR-MOB-006). */
export interface PageHeaderAction {
  label: string;
  onClick?: () => void;
  href?: string;
  /**
   * #22215 — a secondary that is temporarily unavailable (e.g. "Discard changes" with nothing to discard).
   * Rendered disabled both inline and in the ⋯ menu, so moving it into the menu keeps its disabled rule.
   * Applies to button actions; an `href` action is a link and is never disabled.
   */
  disabled?: boolean;
}

export interface PageHeaderProps {
  title: string;
  /** A badge beside the title (Story #28499 — e.g. the Monthly Billing Run's "one page for the month"). */
  badge?: ReactNode;
  description?: string;
  /**
   * The original single action slot. It is never moved into the ⋯ menu: below 768px (phone layout on)
   * it wraps onto its own row under the title, shown in full. Pages that pass a combined node (a period
   * select + a button, week navigation) therefore keep every control visible.
   */
  action?: ReactNode;
  /** The page's primary action. It stays visible at every width (BR-MOB-006). */
  primaryAction?: ReactNode;
  /** Secondary actions — inline from 768px; behind the ⋯ menu below it (phone layout on), same order. */
  secondaryActions?: PageHeaderAction[];
  breadcrumbs?: Array<{ label: string; href?: string }>;
}

const secondaryButtonCls =
  'inline-flex whitespace-nowrap items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';

function SecondaryInline({ action }: { action: PageHeaderAction }) {
  if (action.href) {
    return <a href={action.href} className={secondaryButtonCls}>{action.label}</a>;
  }
  return (
    <button type="button" onClick={action.onClick} disabled={action.disabled} className={secondaryButtonCls}>
      {action.label}
    </button>
  );
}

/** BR-MOB-006 — the ⋯ menu that holds the secondary actions on a phone. */
function OverflowMenu({ actions }: { actions: PageHeaderAction[] }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const itemCls =
    'block w-full min-h-11 px-4 py-3 text-left text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-400 disabled:hover:bg-transparent';

  return (
    <div ref={wrapRef} className="relative shrink-0 md:hidden" data-testid="page-header-more">
      <button
        ref={triggerRef}
        type="button"
        aria-label="More actions"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-md border border-gray-300 bg-white text-lg leading-none text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
      >
        <span aria-hidden>⋯</span>
      </button>
      {open && (
        <div
          role="menu"
          aria-label="More actions"
          className="absolute right-0 z-30 mt-1 min-w-[200px] overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg"
        >
          {actions.map((a) =>
            a.href ? (
              <a key={a.label} role="menuitem" href={a.href} className={itemCls} onClick={() => setOpen(false)}>
                {a.label}
              </a>
            ) : (
              <button
                key={a.label}
                type="button"
                role="menuitem"
                disabled={a.disabled}
                aria-disabled={a.disabled || undefined}
                className={itemCls}
                onClick={() => {
                  a.onClick?.();
                  setOpen(false);
                }}
              >
                {a.label}
              </button>
            ),
          )}
        </div>
      )}
    </div>
  );
}

export function PageHeader({ title, badge, description, action, primaryAction, secondaryActions, breadcrumbs }: PageHeaderProps) {
  const phone = usePhoneLayout();
  const secondaries = secondaryActions ?? [];
  const hasActionGroup = primaryAction !== undefined || secondaries.length > 0;

  return (
    <div className="mb-6">
      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav className="flex mb-2" aria-label="Breadcrumb">
          <ol className="flex items-center space-x-1 text-sm text-gray-500">
            {breadcrumbs.map((crumb, i) => (
              <li key={crumb.label} className="flex items-center">
                {i > 0 && <span className="mx-1">/</span>}
                {crumb.href ? (
                  <a href={crumb.href} className="hover:text-gray-700">{crumb.label}</a>
                ) : (
                  <span className="text-gray-900 font-medium">{crumb.label}</span>
                )}
              </li>
            ))}
          </ol>
        </nav>
      )}
      {/* BR-MOB-006 (phone layout on, below 768px): the row wraps — the title takes its own row clamped
          to two lines, the `action` node wraps under it in full, the primary action stays and the
          secondaries move behind ⋯. Every class added for this is `max-md:`-scoped, so 768px and up
          renders exactly as before; with phone layout off nothing is added at all. */}
      <div
        data-testid="page-header-row"
        className={`flex items-center justify-between${phone ? ' max-md:flex-wrap max-md:gap-3' : ''}`}
      >
        <div className={phone ? 'max-md:min-w-0 max-md:basis-full' : undefined}>
          {badge === undefined ? (
            <h1
              className={`text-2xl font-semibold tracking-tight text-gray-900${phone ? ' max-md:line-clamp-2 max-md:break-words' : ''}`}
            >
              {title}
            </h1>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <h1
                className={`text-2xl font-semibold tracking-tight text-gray-900${phone ? ' max-md:line-clamp-2 max-md:break-words' : ''}`}
              >
                {title}
              </h1>
              {badge}
            </div>
          )}
          {description && <p className="mt-1 text-sm text-gray-500">{description}</p>}
        </div>
        {action !== undefined &&
          (phone ? (
            <div
              data-testid="page-header-action"
              className="max-md:flex max-md:w-full max-md:min-w-0 max-md:flex-wrap max-md:items-center max-md:gap-2"
            >
              {action}
            </div>
          ) : (
            action
          ))}
        {hasActionGroup && (
          <div data-testid="page-header-actions" className={`flex shrink-0 items-center gap-2 md:whitespace-nowrap${phone ? ' max-md:w-full' : ''}`}>
            {secondaries.map((a) => (
              <span key={a.label} data-testid="page-header-secondary" className={phone ? 'max-md:hidden' : undefined}>
                <SecondaryInline action={a} />
              </span>
            ))}
            {primaryAction !== undefined && (
              <div
                data-testid="page-header-primary"
                className={phone ? 'max-md:min-w-0 max-md:flex-1 max-md:[&>*]:min-h-11 max-md:[&>*]:w-full' : undefined}
              >
                {primaryAction}
              </div>
            )}
            {phone && secondaries.length > 0 && <OverflowMenu actions={secondaries} />}
          </div>
        )}
      </div>
    </div>
  );
}
